import classnames from 'classnames';
import PropTypes from 'prop-types';
import React, { useMemo, useLayoutEffect, useRef, useState, useEffect } from 'react';

import styles from './styles.module.scss';
import Button from '../Button/index.ts';
import {
  useBoolean,
  addVariants,
  useClickOutside,
  useSpacing,
  useWindowSize,
  usePrevious
} from '../utils/index.ts';

export const COLORS = ['white', 'gray', 'dark', 'red', 'blue', 'green', 'orange'];
export const VARIANTS = COLORS.concat([
  'default',
  'dense',
  'arrow',
  'no-padding',
  'half-padding',
  'shadow',
  'full-height',
  'full-width',
  'fade'
]);

const DIRECTION = {
  X: 'X',
  Y: 'Y'
};
function prefixedTransform(translatePercentage, direction) {
  let transform;
  if (direction === DIRECTION.X) {
    transform = `translateX(${translatePercentage}%)`;
  } else {
    transform = `translateY(${translatePercentage}%)`;
  }
  const cssPrefixes = ['', 'webkit', 'moz', 'ms'];
  return cssPrefixes.reduce((acc, prefix) => ({ [`${prefix}transform`]: transform, ...acc }), {});
}

const POSITION_PLACEMENTS = Object.freeze({
  right: {
    bottom: '50%',
    left: '100%',
    ...prefixedTransform(50, DIRECTION.Y)
  },
  'right-start': {
    bottom: 0,
    left: '100%'
  },
  'right-end': {
    top: 0,
    left: '100%'
  },
  left: {
    bottom: '50%',
    right: '100%',
    ...prefixedTransform(50, DIRECTION.Y)
  },
  'left-start': {
    bottom: 0,
    right: '100%'
  },
  'left-end': {
    top: 0,
    right: '100%'
  },
  bottom: {
    top: '100%',
    right: '50%',
    ...prefixedTransform(50, DIRECTION.X)
  },
  'bottom-start': {
    top: '100%',
    right: 0
  },
  'bottom-end': {
    top: '100%',
    left: 0
  },
  top: {
    bottom: '100%',
    right: '50%',
    ...prefixedTransform(50, DIRECTION.X)
  },
  'top-start': {
    bottom: '100%',
    right: 0
  },
  'top-end': {
    bottom: '100%',
    left: 0
  }
});

export const PLACEMENTS = Object.keys(POSITION_PLACEMENTS);

function useRepositioning(attemptPlacement, isOpen) {
  const popupRef = useRef();
  const { width: windowWidth, height: windowHeight } = useWindowSize();
  const [placement, setPlacement] = useState(attemptPlacement);
  const previousPlacement = usePrevious(placement);
  const alreadyMounted = useRef(false);

  useEffect(() => {
    if (alreadyMounted.current) {
      // update the placement if updated
      setPlacement(attemptPlacement);
    } else {
      alreadyMounted.current = true;
    }
  }, [attemptPlacement]);

  useLayoutEffect(() => {
    const [direction, startOrEnd] = attemptPlacement.split('-');
    if (popupRef.current) {
      const { x, y, height, width } = popupRef.current.getBoundingClientRect();
      const nextPlacements = [direction, startOrEnd];
      const offLeft = x < 0;
      const offTop = y < 0;
      const offBottom = y + height > windowHeight;
      const offRight = x + width > windowWidth;
      if ((offLeft || offRight) && (offBottom || offTop)) {
        // No suitable spot
        return;
      }
      if ([offLeft, offRight, offTop, offBottom].every(o => o === false)) {
        // No need to move not off any side
        return;
      }

      if (direction === 'bottom' || direction === 'top') {
        if (offRight) {
          nextPlacements[1] = 'start';
        }
        if (offLeft) {
          nextPlacements[1] = 'end';
        }
      } else {
        if (offTop) {
          nextPlacements[1] = 'start';
        }
        if (offLeft) {
          nextPlacements[1] = 'end';
        }
      }

      const nextPlacement = nextPlacements[0] + (nextPlacements[1] ? `-${nextPlacements[1]}` : '');
      if (nextPlacement !== placement) {
        setPlacement(nextPlacement);
      }
    }
  }, [attemptPlacement, isOpen, placement, previousPlacement, windowHeight, windowWidth]);

  return [popupRef, placement];
}

const Popover = ({
  activator: activatorRaw,
  activatorIcon,
  placement: placementFromProps,
  placementRepositioningEnabled = true,
  children,
  className,
  containerClassName,
  variant,
  isOpen: isOpenFromProps,
  isOpenByDefault,
  onChange,
  spacing,
  style,
  ...props
}) => {
  const {
    toggle: toggleOpen,
    value: isOpen,
    set: setOpen,
    setFalse: close,
    setTrue: open
  } = useBoolean(isOpenByDefault, { onChange, overrideWith: isOpenFromProps });
  const [popupRef, placementRepositioned] = useRepositioning(placementFromProps, isOpen);
  const placement = placementRepositioningEnabled ? placementRepositioned : placementFromProps;
  const spacingStyle = useSpacing(spacing, {
    style: {
      ...style,
      ...POSITION_PLACEMENTS[placement]
    }
  });
  const arrowSpacingStyle = useSpacing(spacing, {
    style: POSITION_PLACEMENTS[placement]
  });
  const nodeRef = useClickOutside(close, {
    active: isOpen,
    excludeScrollbar: true
  });
  const childrenEl = useMemo(
    () =>
      typeof children === 'function'
        ? children({
            isOpen,
            toggleOpen,
            setOpen,
            open,
            close
          })
        : children,
    [children, isOpen, setOpen, open, close, toggleOpen]
  );

  const activatorEl = useMemo(() => {
    let activator = activatorRaw;

    if (!activatorRaw) {
      activator = (
        <Button
          onClick={toggleOpen}
          icon={activatorIcon}
          variant={['flat', isOpen && 'active']}
          className={classnames(
            addVariants('aglmd-popover__activator', styles, isOpen && 'active')
          )}
        />
      );
    } else if (typeof activatorRaw === 'function') {
      activator = activatorRaw({
        isOpen,
        toggleOpen,
        setOpen,
        open,
        close
      });
    }

    return activator;
  }, [activatorRaw, toggleOpen, activatorIcon, isOpen, setOpen, open, close]);
  let arrowEl;

  if (variant.includes('arrow')) {
    arrowEl = (
      <div
        className={classnames(
          addVariants(
            'aglmd-popover-arrow',
            styles,
            variant,
            placement,
            isOpen ? 'active' : 'inactive'
          )
        )}
        style={arrowSpacingStyle}
      />
    );
  }

  return (
    <div
      className={classnames(
        addVariants('aglmd-popover', styles, variant, isOpen ? 'active' : 'inactive'),
        containerClassName
      )}
      ref={nodeRef}
      {...props}
    >
      {activatorEl}
      <div
        className={classnames(
          addVariants(
            'aglmd-popover-container',
            styles,
            variant,
            placement,
            isOpen ? 'open' : 'closed'
          ),
          className
        )}
        style={spacingStyle}
        data-placement={placement}
        ref={popupRef}
      >
        {isOpen ? childrenEl : null}
      </div>
      {arrowEl}
    </div>
  );
};

Popover.propTypes = {
  /**
   * Allows specifying an icon to designate as the popover activator
   * @deprecated Use `activator` instead
   */
  activatorIcon: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.string.isRequired),
    PropTypes.shape({
      iconName: PropTypes.string.isRequired,
      prefix: PropTypes.string.isRequired
    })
  ]),
  /**
   * Allows specifying a specific element to render as the activator
   * called with {isOpen, toggleOpen, setOpen}
   */
  activator: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  /**
   * The direction which the popover should popover toward
   */
  placement: PropTypes.oneOf(PLACEMENTS),
  /**
   * Whether auto placement (repositioning) is disabled
   */
  placementRepositioningEnabled: PropTypes.bool,
  /**
   * The popover content itself
   */
  children: PropTypes.any.isRequired,
  /**
   * Decides the variant of the button
   */
  variant: PropTypes.oneOfType([
    PropTypes.oneOf(VARIANTS),
    PropTypes.bool,
    PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.oneOf(VARIANTS), PropTypes.bool]))
  ]),
  /**
   * Allow customization of the spacing between the popover and activator. See `Spacer` Component
   */
  spacing: PropTypes.shape({
    top: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
    bottom: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
    left: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
    right: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
    x: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
    y: PropTypes.oneOfType([PropTypes.number, PropTypes.bool])
  }),
  /**
   * Custom styles to apply, use sparingly
   */
  style: PropTypes.shape({}),
  /**
   * Additional classnames to apply
   */
  className: PropTypes.string,
  /**
   * Additional classnames to apply to the container
   */
  containerClassName: PropTypes.string,
  /**
   * Whether the popover is open or not, state must be handled by caller
   */
  isOpen: PropTypes.bool,
  /**
   * Whether the popover is "popped open" by default
   */
  isOpenByDefault: PropTypes.bool,
  onChange: PropTypes.func
};

Popover.defaultProps = {
  activator: false,
  activatorIcon: null,
  className: '',
  containerClassName: '',
  placement: 'bottom',
  variant: 'default',
  isOpen: null,
  isOpenByDefault: false,
  onChange: () => 0,
  spacing: {},
  style: {}
};

export default Popover;
